<template>
  <div class="charts">
    <v-overlay :value="loading" absolute z-index="1" class="">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <highcharts
      v-if="!loading"
      :options="chartOptions"
      ref="highcharts"
      class="charts"
    ></highcharts>
  </div>
</template>

<script>
import moment from "moment";
export default {
  name: "MeterChart",
  props: {
    meter: {
      type: Object
    },
    meterUnits: {
      type: Array
    },
    project_id: {
      type: String
    },
    options: {
      type: Object
    }
  },
  data() {
    return {
      records: {},
      series: [],
      loading: false,
      project: {},
      chartOptions: {
        credits: {
          enabled: false
        },
        title: {
          text: ""
        }
      }
    };
  },
  computed: {
    optionsComputed() {
      return (
        this.options &&
        `${this.options.date_from}${this.options.date_to}${this.options.interval}${this.options.rawRecord}`
      );
    },
    symbols() {
      return this.meterUnits.reduce((ac, i) => {
        let index = ac.findIndex(
          u =>
            i._visualization.symbol_name === u.symbol_name &&
            i._visualization.symbol === u.symbol
        );
        if (index >= 0) {
          ac[index].meterUnits.push(i.id);
        } else {
          ac.push({
            meterUnits: [i.id],
            symbol_name: i._visualization.symbol_name,
            symbol: i._visualization.symbol
          });
        }
        return ac;
      }, []);
    }
  },
  methods: {
    getRecords() {
      this.records = {};
      this.update();
      this.loading = true;
      if (this.meterUnits && this.meterUnits.length) {
        let from =
          this.options.date_from &&
          moment
            .utc(
              moment.tz(
                `${this.options.date_from} ${
                  this.options.time_mode === "last"
                    ? moment().format("HH:mm")
                    : "00:00"
                }`,
                this.project.timezone
              )
            )
            .format();
        let to =
          this.options.date_to &&
          moment
            .utc(
              moment
                .tz(this.options.date_to, this.project.timezone)
                .endOf("day")
            )
            .format();
        this.$store.dispatch(
          "projectMeteringStore/meterUnitRecordStore/getList",
          {
            params: {
              project_id: this.project_id,
              interval: this.options.interval || "day",
              rawRecord: this.options.rawRecord,
              differencesForAccumulated: 1,
              sumTariffs: 1,
              limit: -1,
              includeReplaced: true,
              applyDisplayFactor: true,
              filters: {
                meter_unit: this.meterUnits.map(i => i.id).join(";"),
                time: {
                  from,
                  to
                }
              }
            },
            onSuccess: res => {
              this.addRecords(res.list);
              this.loading = false;
            }
          }
        );
      } else {
        this.loading = false;
      }
    },
    addRecords(list) {
      let records = {};
      for (let item of Object.values(list)) {
        let data = [
          Date.parse(
            moment
              .tz(item.time, this.project && this.project.timezone)
              .format("YYYY-MM-DDTHH:mm:ss")
          ),
          item.value
        ];
        let key = item._meter_unit && item._meter_unit.id;
        records[key] && records[key].data
          ? records[key].data.push(data)
          : (records[key] = { data: [data] });
      }
      for (let id of Object.keys(records)) {
        let meterUnit = this.meterUnits.find(i => i.id === id);
        let index = this.symbols.findIndex(i => i.meterUnits.includes(id));
        records[id] = {
          name: meterUnit && meterUnit.name,
          type:
            (meterUnit && meterUnit._visualization.chart_type) || "areaspline",
          yAxis: index,
          fillOpacity: 0.1,
          showInNavigator: true,
          dataGrouping: {
            approximation: "sum",
            groupPixelWidth: 75
          },
          tooltip: {
            valueSuffix: this.symbols[index].symbol
          },
          ...{
            color: meterUnit && meterUnit._visualization.chart_series_color,
            fillColor:
              meterUnit && meterUnit._visualization.chart_series_fill_color
          },
          ...records[id]
        };
      }

      this.$set(this, "records", records);
      this.series = Object.values(this.records);
      this.update();
    },
    update() {
      if (!this.$i18n) {
        return;
      }
      const self = this;
      let symbol_name = self.symbols.map(i => i.symbol_name);
      let symbol = self.symbols.map(i => i.symbol);

      let text = `${symbol_name.join(",")} | ${this.$t(
        `analysis.${this.options.interval}`
      ).toLowerCase()} | ${
        this.options.dateTitle
          ? this.LowerFirstLetter(this.$t(this.options.dateTitle))
          : this.$dateTime(this.options.date_from, "short") +
            " - " +
            this.$dateTime(this.options.date_to, "short")
      }`;
      let formNumber = function(number) {
        return self.$number(number) || 0;
      };
      let zoomType = this.$vuetify.breakpoint.mdAndDown ? "" : "x";
      let clickDetected = false;
      let resetZoom = this.resetZoom;
      this.chartOptions = {
        credits: {
          enabled: false
        },
        chart: {
          type: "spline",
          zoomType,
          resetZoomButton: {
            theme: {
              zIndex: 2,
              visibility: "hidden"
            }
          },
          events: {
            click: function() {
              if (clickDetected) {
                resetZoom();
                clickDetected = false;
              } else {
                clickDetected = true;
                setTimeout(function() {
                  clickDetected = false;
                }, 300);
              }
            }
          }
        },
        pointer: {
          zoomX: false,
          zoomHor: false
        },
        title: {
          text: text,
          align: "left",
          x: 5,
          style: {
            fontFamily: `"Roboto", sans-serif`,
            fontSize: "14px",
            fontWeight: "500"
          }
        },
        rangeSelector: {
          enabled: false
        },
        navigator: {
          enabled: false,
          maskFill: "rgba(25,25,25,0.16)"
        },
        tooltip: {
          formatter: function() {
            let point = this.point;
            const tooltipOptions = point.series.tooltipOptions;

            const valuePrefix = tooltipOptions.valuePrefix || "";
            const valueSuffix = tooltipOptions.valueSuffix || "";
            return `<b>${self.$dateTime(this.x, "long")} </b><br>
                    ${point.series.name} : ${valuePrefix} ${formNumber(
              point.y
            )} ${valueSuffix}`;
          }
        },
        yAxis: symbol.map((i, index) => ({
          title: {
            text: null
          },
          labels: {
            formatter: function() {
              return formNumber(this.value) + " " + i;
            }
          },
          opposite: index % 2 == 1
        })),
        xAxis: {
          ordinal: false,
          labels: {
            formatter: function() {
              return self.$dateTime(this.value, "short");
            }
          }
        },
        series: this.series
      };
      this.resetZoom();
    },
    resetZoom() {
      this.$refs.highcharts &&
        this.$refs.highcharts.chart &&
        this.$refs.highcharts.chart.zoomOut();
    },
    LowerFirstLetter(string) {
      return string[0].toLowerCase() + string.slice(1);
    }
  },
  watch: {
    meter: {
      handler: function() {
        this.getRecords();
        this.resetZoom();
      },
      deep: true
    },
    meterUnits: {
      handler: function() {
        this.getRecords();
      },
      deep: true
    },
    optionsComputed() {
      this.getRecords();
    }
  },
  mounted() {
    window.dispatchEvent(new Event("resize"));
    this.resetZoom();
    this.getRecords();
    this.$store.dispatch("projectStore/get", {
      id: this.project_id,
      onSuccess: project => {
        this.project = project;
      }
    });
  }
};
</script>

<style scoped>
.charts {
  height: 400px;
  width: 100%;
  max-width: 100%;
}
</style>
